// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import { borderWidth } from 'polished';
import { withHandlers } from 'recompose';

import AdvertHandler from 'app/containers/parts/AdvertHandler';
import SimpleSticky from 'app/components/commons/SimpleSticky';
import t from 'app/utils/translate';
import Responsive from 'app/components/responsive/Responsive';
import Button from 'app/components/commons/Button';
import FixToViewport from 'app/components/commons/FixToViewport';
import OneScoreWidget from 'app/widgets/one-sport/OneScoreWidget';
import LineUpAllocation from './LineUpAllocation';
import LineUpSection from './LineUpSection';
import LineUpTable from './LineUpTable';
import SelectedEntityRow from './SelectedEntityRow';
import WidgetEntityRow from './WidgetEntityRow';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  activeBonus?: string,
  bonuses: Array<Object>,
  canCompleteTeam: boolean,
  disableSelectLineup?: boolean,
  displayWidget: boolean,
  editMode?: boolean,
  games?: Array<*>,
  enableSmartLineUp?: boolean,
  loadEntry: boolean,
  isAnimating: boolean,
  isLocked: boolean,
  isRacing: boolean,
  isSubmitting: boolean,
  isUpdating: boolean,
  editLineup?: boolean,
  numLocked: number,
  numSelected: number,
  offsetTop: number,
  onAutoFill: () => void,
  onBonusAdd: (key: string, playerId: string) => void,
  onBonusSelect: (id: number) => void,
  onClear: () => void,
  onGameSelect: string => void,
  onLock: () => void,
  onPositionSelect: string => void,
  onRemove: () => void,
  onSubmit: () => void,
  onUnlock: () => void,
  onUpdate: () => void,
  onWidgetClose: () => void,
  onWidgetOpen: (string, string) => void,
  playerBonuses: {
    [key: string]: {
      name: string,
      id: string
    }
  },
  positions: Array<Object>,
  pxc?: Object,
  requiredSelections: number,
  salaryCap?: number,
  salaryExpenditure: number,
  widgetPlayerId?: string,
  isRestricted?: boolean
};

const OuterSticky = styled(SimpleSticky)(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100vh'
  }
}));

const InnerSticky = styled(SimpleSticky)({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0
});

const Head = styled.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    backgroundColor: 'none',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0
  }
}));

const Body = styled.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    flex: '0 1 auto',
    overflowX: 'hidden'
  }
}));

const Foot = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  backgroundColor: theme.color.panel,
  [theme.mediaQuery.desktopAndUp]: {
    flex: '0 0 auto',
    paddingTop: theme.spacing.tiny
  }
}));

const BonusList = styled.ul(({ theme }) => ({
  borderColor: theme.color.basis2,
  borderStyle: 'solid',
  ...borderWidth(0, 0, 1),
  display: 'flex',
  justifyContent: 'space-between',
  margin: 0,
  padding: 0,
  [theme.mediaQuery.desktopAndUp]: {
    paddingBottom: theme.spacing.tiny,
    borderBottom: 'none'
  }
}));

const BonusItem = styled.li({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  listStyleType: 'none'
});

class LineUp extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    activeBonus: undefined,
    enableSmartLineUp: false,
    disableSelectLineup: false,
    editMode: false,
    editLineup: undefined,
    games: [],
    pxc: undefined,
    salaryCap: undefined,
    widgetPlayerId: undefined,
    isRestricted: false
  };

  toggleWidget = (idStr, positionId) => {
    const { onWidgetOpen, onWidgetClose, widgetPlayerId } = this.props;
    if (idStr === widgetPlayerId) {
      onWidgetClose();
    } else {
      onWidgetOpen(idStr, positionId);
    }
  };

  showSubmitButtonText = () => {
    const { isLocked, editLineup, loadEntry } = this.props;

    if (isLocked) {
      return t('Locked');
    }
    return loadEntry || editLineup ? t('Update LineUp') : t('Submit LineUp');
  };

  render() {
    const {
      activeBonus,
      bonuses,
      canCompleteTeam,
      disableSelectLineup,
      displayWidget,
      editMode,
      games,
      loadEntry,
      isAnimating,
      isLocked,
      isRacing,
      isSubmitting,
      isUpdating,
      numLocked,
      numSelected,
      enableSmartLineUp,
      requiredSelections,
      offsetTop,
      onAutoFill,
      onBonusAdd,
      onBonusSelect,
      onClear,
      onGameSelect,
      onPositionSelect,
      onLock,
      onRemove,
      onSubmit,
      onUnlock,
      onUpdate,
      onWidgetClose,
      positions,
      playerBonuses,
      pxc,
      salaryCap,
      salaryExpenditure,
      widgetPlayerId,
      isRestricted
    } = this.props;

    const canSubmit =
      numSelected === requiredSelections &&
      positions.every(p => p.filled && !p.hasError) &&
      bonuses.every(b => b.selected) &&
      salaryExpenditure <= salaryCap &&
      !isSubmitting &&
      !isUpdating &&
      !isLocked &&
      !isRestricted;

    const remainingSelections = requiredSelections - numSelected;

    return (
      <Responsive desktop>
        {isDesktop => (
          <OuterSticky isStuckFunc={isStuck => isStuck && isDesktop} offsetTop={offsetTop}>
            <InnerSticky isStuckFunc={() => !isDesktop} offsetTop={offsetTop}>
              <Head>
                <LineUpAllocation
                  bonuses={bonuses}
                  currentStep={numSelected}
                  disableAutoFill={
                    !canCompleteTeam ||
                    (numSelected === requiredSelections && numLocked === numSelected)
                  }
                  disableSelectLineup={disableSelectLineup}
                  enableSmartLineUp={enableSmartLineUp}
                  editMode={editMode}
                  isAnimating={isAnimating}
                  pxc={pxc}
                  numUnlocked={requiredSelections - numLocked}
                  onAutoFill={onAutoFill}
                  onClear={onClear}
                  salaryCap={salaryCap}
                  salaryExpenditure={salaryExpenditure}
                  totalSteps={requiredSelections}
                />
              </Head>
            </InnerSticky>
            <Body>
              <LineUpTable
                positions={positions}
                renderPosition={pos => (
                  <LineUpSection
                    key={`${pos.id}_${pos.isTeam}`}
                    abbr={pos.abbr}
                    filled={pos.filled}
                    remaining={
                      pos.shareQuota && pos.isTeam
                        ? 0
                        : Math.min(pos.max - pos.selected, remainingSelections)
                    }
                    hasError={pos.hasError}
                    games={games}
                    id={pos.id}
                    isRacing={isRacing}
                    name={pos.name}
                    selections={pos.selections}
                    onGameSelect={onGameSelect}
                    onPositionSelect={onPositionSelect}
                    renderEntity={(entity, i) => (
                      <React.Fragment
                        // [MC] remounting each row during an auto-fill animation is slow!
                        // more performant to use an index for the key
                        key={i}
                      >
                        <SelectedEntityRow
                          awayTeam={entity.awayAbbr}
                          activeBonus={activeBonus}
                          bonus={playerBonuses[entity.id]}
                          disabled={entity.disabled}
                          gameId={entity.gameId}
                          gameOrder={entity.gameOrder}
                          image={entity.imageURI}
                          initials={entity.initials}
                          showWidget={displayWidget && entity.id === widgetPlayerId}
                          homeTeam={entity.homeAbbr}
                          id={entity.id}
                          idStr={entity.idStr}
                          isAnimating={isAnimating}
                          isHome={entity.isHome}
                          isRacing={isRacing}
                          isScratched={entity.isScratched}
                          labels={entity.labels}
                          locked={entity.locked}
                          name={entity.name}
                          onBonusAdd={onBonusAdd}
                          onLock={onLock}
                          onRemove={onRemove}
                          onUnlock={onUnlock}
                          onWidgetClick={this.toggleWidget}
                          place={entity.placeProjected}
                          positionLabel={pos.abbr}
                          positionId={pos.id}
                          reverseOrder={entity.reverseOrder}
                          rider={entity.rider}
                          riderImage={entity.riderImage}
                          salary={getFactoredEntityPositionSalary(
                            entity.salary,
                            entity.positions,
                            pos.key
                          )}
                          trainer={entity.trainer}
                          win={entity.winProjected}
                          showButtons
                        />
                        {!isDesktop && (
                          <OneScoreWidget
                            isOpen={widgetPlayerId === entity.id}
                            isTeam={entity.isTeam}
                            id={entity.id}
                            onClose={onWidgetClose}
                            renderEntity={() => (
                              <WidgetEntityRow
                                key={entity.idStr}
                                id={entity.id}
                                idStr={entity.idStr}
                                isTeam={entity.isTeam}
                                onRemove={(...args) => {
                                  onRemove(...args);
                                  onWidgetClose(entity.id);
                                }}
                                selectedPosition={pos.id}
                                selected
                              />
                            )}
                          />
                        )}
                      </React.Fragment>
                    )}
                  />
                )}
              />
            </Body>
            <FixToViewport disabled placement="bottom" fillSpace>
              <Foot>
                {bonuses.length > 0 && (
                  <BonusList>
                    {bonuses.map(bonus => (
                      <BonusItem key={bonus.key}>
                        <Button
                          block
                          autoBorderRadius={isDesktop}
                          size="small"
                          kind="info"
                          onClick={() => onBonusSelect(bonus.id)}
                          disabled={numSelected === 0}
                        >
                          {bonus.selected ? t('Change') : t('Select')} {bonus.description}
                        </Button>
                      </BonusItem>
                    ))}
                  </BonusList>
                )}
                {!isDesktop && <AdvertHandler zone="challengeFooter" />}
                <Button
                  autoBorderRadius={false}
                  block
                  disabled={!canSubmit}
                  kind="primary"
                  onClick={loadEntry ? onUpdate : onSubmit}
                  size="medium"
                >
                  {this.showSubmitButtonText()}
                </Button>
              </Foot>
            </FixToViewport>
          </OuterSticky>
        )}
      </Responsive>
    );
  }
}

const enhancer = withHandlers({
  onRemove: ({ onRemove }) => (entity, position) => {
    onRemove(entity, position, true);
  }
});

export default enhancer(LineUp);
